import type {
  SurveyInsightsBlock,
  SurveyInsightsGranularity,
  SurveyInsightsPeriod,
  SurveyInsightsTrendBucket,
} from '../hubOwner/types';

export interface FacilityInfo extends Facility {
  admins: Admin[];
  invites: FacilityAdminInvite[];
}

export interface Facility {
  id: number;
  name: string;
  med_window_time_sec: number;
  created_at: string;
  address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  email?: string;
  total_devices?: number;
  total_patients?: number;
  status?: string;
}

export interface Admin {
  name: string;
  email: string;
}

export interface FacilityAdminInvite {
  email: string;
  facility_id?: number;
  facility_role_id: number;
  expires_at?: string;
  first_name?: string;
  last_name?: string;
}

export interface NewFacility {
  name: string;
  med_window_time_sec?: number;
}

export interface UpdateFacility {
  med_window_time_sec?: number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  is_headquarter?: boolean;
  parent_facility_id?: number;
}

export type FacilityUpdateType =
  'LOCATION_MED_WINDOW' | 'LOCATION_INFO' | 'FACILITY_INFO';

export interface UpdateParentFacility {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  phone?: string;
  email?: string;
}

export interface FacilityAdminRegisterData {
  first_name: string;
  last_name: string;
  password: string;
  password2: string;
  token: string;
}

export interface FacilityDevice {
  id: number;
  serial_number: string;
  owner_name?: string;
  synced_by_name?: string;
  owner?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  synced_by?: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  hardware_version?: string;
  software_version?: string;
  software_channel_id?: number;
  pinned_software_version_id?: number;
  pinned_software_version_name?: string;
  facility_id?: number;
  facility_name?: string;
  device_status?: string;
}

export interface Caregiver {
  customer_id: number;
  first_name: string;
  last_name: string;
  facility_role_id: number;
  last_login_at: string;
}

export interface CaregiverDetails extends Caregiver {
  first_name: string;
  last_name: string;
  email: string; // username is email
  username: string;
  phone: string;
  facility_role_id: number;
  combined_name?: string; // F.E is handling this
}

export interface FacilityDevicesState {
  facility_id: number;
  devices: FacilityDevice[];
}

export interface UpdateCaregiverData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  facility_role_id?: number;
}

export interface NewCaregiverData {
  first_name: string;
  last_name: string;
  username: string; // this is email
  facility_role_id: number;
  phone: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipcode: number;
}

export enum FacilityDeviceAssociationAction {
  LINK = 'LINK',
  UNLINK = 'UNLINK',
}

export type FacilityStatus = 'ok' | 'warning' | 'critical';
export interface Location {
  id: number;
  name: string;
  created_at: string;
  created_by: number;
  deleted_at?: string;
  deleted_by?: number;
  med_window_time_sec: number;
  email: string;
  phone: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  country: string;
  zip_code: string;
  last_updated: string;
  last_updated_by: number;
  is_headquarter: boolean;
  parent_facility_id: number;
  status: FacilityStatus;
  total_devices: number;
  total_patients: number;
}
export interface ParentFacility {
  parent_facility_id: number;
  parent_facility_name: string;
  parent_facility_address1: string;
  parent_facility_address2: string;
  parent_facility_city: string;
  parent_facility_state: string;
  parent_facility_zip_code: string;
  parent_facility_phone: string;
  parent_facility_created_at: string;
  parent_facility_created_by: number;
  locations: Location[];
  status: FacilityStatus;
  total_devices: number;
  total_locations: number;
  total_patients: number;
}

export interface AddParentFacilityAdminData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  parent_facility_role_id: string;
}

export interface CreateParentFacilityData {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  country?: string;
  zip_code: string;
  phone: string;
}

export interface ParentFacilityResponse {
  parent_facility_id: number;
}

export interface CreateLocationData {
  name: string;
  med_window_time_sec: number;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  parent_facility_id: string;
  is_headquarter?: boolean;
}

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export interface Staff {
  role_id: number;
  first_name: string;
  last_name: string;
  customer_id: number;
  email: string;
  last_login_at: string;
  role_level: string;
  location: string[];
  role: string;
}

export interface Patient {
  first_name: string;
  last_name: string;
  customer_id: number;
  room_number: string;
  last_login_at: string | null;
  missed_event_last_24h: number;
  location_id: number;
  next_med_event: string | null;
  status: 'ok' | 'warning' | 'critical';
}

export interface Device {
  customer_id: number;
  first_name: string;
  last_name: string;
  serial_number: string;
  software_version: string;
  location_id: number;
  last_seen_online: string | null;
  device_status: 'online' | 'offline' | 'unassigned';
}

export interface Note {
  note_id: number;
  note: string;
  location_id: number | null;
  created_by: number;
  created_at: string;
  location_name: string | null;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
}
export interface NoteActionStatus {
  success: boolean;
  message: string;
  show: boolean;
}

export interface FacilityStore {
  facilities: FacilityInfo[] | null;
  facilityInfo: Facility | null;
  facilityDevices?: FacilityDevicesState;
  goToFacilityPage: boolean;
  error: any | null;
  isLoading: boolean;
  showSuccessAlert: boolean;
  showErrorAlert: boolean;
  showDuplicateAlert: boolean;
  showDuplicateAlertFacility: boolean;
  showDuplicateAlertCareTeam: boolean;
  caregivers: Caregiver[];
  caregiver: CaregiverDetails | null;
  addCaregiverSuccess: boolean;
  deleteCaregiverSuccess: boolean;
  inviteFacilityAdminSuccess: boolean;
  updateCaregiverSuccess: boolean;
  parentFacilities: ParentFacility[] | null;
  addParentFacilityAdminStatus: boolean | null;
  facilitiesBreadcrumbs: BreadcrumbItem[];
  staffList: Staff[];
  patientsList: Patient[];
  devicesList: Device[];
  notesList: Note[];
  noteActionStatus: NoteActionStatus;
  deactivateFacilityStatus: boolean | null;
  editStaffStatus: boolean | null;
  // GET /facility/:id/survey_insights, cached for one location and window
  // (same shape and request-id guard as hubOwner.ownerSurveyInsights).
  facilitySurveyInsights: FacilitySurveyInsightsState | null;
  facilitySurveyInsightsLoading: boolean;
  facilitySurveyInsightsError: string | null;
  facilitySurveyInsightsRequestId: number;
}

export enum FacilityStoreActionTypes {
  FACILITY_ADDED = 'FACILITY_ADDED',
  FACILITY_API_ERR = 'FACILITY_API_ERR',
  FACILITY_SET_LOAD_STATE = 'FACILITY_SET_LOAD_STATE',
  POPULATE_FACILITIES = 'POPULATE_FACILITIES',
  POPULATE_FACILITY_DEVICES = 'POPULATE_FACILITY_DEVICES',
  CLEAR_FACILITY_DEVICES = 'CLEAR_FACILITY_DEVICES',
  POPULATE_FACILITY_INFO = 'POPULATE_FACILITY_INFO',
  SET_ERROR_ALERT = 'SET_ERROR_ALERT',
  SET_FACILITY_ADMINS = 'SET_FACILITY_ADMINS',
  SET_FACILITY_INVITES = 'SET_FACILITY_INVITES',
  SET_SUCCESS_ALERT = 'SET_SUCCESS_ALERT',
  SET_DUPLICATE_ALERT = 'SET_DUPLICATE_ALERT',
  SET_DUPLICATE_ALERT_FACILITY = 'SET_DUPLICATE_ALERT_FACILITY',
  SET_DUPLICATE_ALERT_CARETEAM = 'SET_DUPLICATE_ALERT_CARETEAM',
  POPULATE_CAREGIVERS = 'POPULATE_CAREGIVERS',
  POPULATE_CAREGIVER_BY_ID = 'POPULATE_CAREGIVER_BY_ID',
  UPDATE_CAREGIVER_BY_ID = 'UPDATE_CAREGIVER_BY_ID',
  DELETE_CAREGIVER_BY_ID = 'DELETE_CAREGIVER_BY_ID',
  SET_DELETE_CAREGIVER_SUCCESS = 'SET_DELETE_CAREGIVER_SUCCESS',
  ADD_CAREGIVER = 'ADD_CAREGIVER',
  SET_ADD_CAREGIVER_SUCCESS = 'SET_ADD_CAREGIVER_SUCCESS',
  RESET_ADD_CAREGIVER_SUCCESS = 'RESET_ADD_CAREGIVER_SUCCESS',
  UPDATE_FACILITY_DEVICE_STATUS = 'UPDATE_FACILITY_DEVICE_STATUS',
  INVITE_FACILITY_ADMIN = 'INVITE_FACILITY_ADMIN',
  SET_INVITE_FACILITY_ADMIN_SUCCESS = 'SET_INVITE_FACILITY_ADMIN_SUCCESS',
  SET_UPDATE_CAREGIVER_SUCCESS = 'SET_UPDATE_CAREGIVER_SUCCESS',
  RESET_SUCCESS_STATES = 'RESET_SUCCESS_STATES',
  POPULATE_PARENT_FACILITIES = 'POPULATE_PARENT_FACILITIES',
  ADD_PARENT_FACILITY_ADMIN_STATUS = 'ADD_PARENT_FACILITY_ADMIN_STATUS',
  SET_FACILITIES_BREADCRUMBS = 'SET_FACILITIES_BREADCRUMBS',
  POPULATE_STAFF_LIST = 'POPULATE_STAFF_LIST',
  POPULATE_PATIENTS_LIST = 'POPULATE_PATIENTS_LIST',
  POPULATE_DEVICES_LIST = 'POPULATE_DEVICES_LIST',
  POPULATE_NOTES_LIST = 'POPULATE_NOTES_LIST',
  ADD_NOTE = 'ADD_NOTE',
  SET_NOTE_ACTION_STATUS = 'SET_NOTE_ACTION_STATUS',
  CLEAR_NOTE_ACTION_STATUS = 'CLEAR_NOTE_ACTION_STATUS',
  DEACTIVATE_FACILITY_STATUS = 'DEACTIVATE_FACILITY_STATUS',
  CLEAR_DEACTIVATE_FACILITY_STATUS = 'CLEAR_DEACTIVATE_FACILITY_STATUS',
  EDIT_STAFF_STATUS = 'EDIT_STAFF_STATUS',
  REQUEST_FACILITY_SURVEY_INSIGHTS = 'REQUEST_FACILITY_SURVEY_INSIGHTS',
  FETCH_FACILITY_SURVEY_INSIGHTS_SUCCESS = 'FETCH_FACILITY_SURVEY_INSIGHTS_SUCCESS',
  FETCH_FACILITY_SURVEY_INSIGHTS_FAILURE = 'FETCH_FACILITY_SURVEY_INSIGHTS_FAILURE',
}

export type FacilityAddedType = {
  type: FacilityStoreActionTypes.FACILITY_ADDED;
};

export type ApiErrorType = {
  type: FacilityStoreActionTypes.FACILITY_API_ERR;
  payload: { error: any };
};

export type SetLoadStateType = {
  type: FacilityStoreActionTypes.FACILITY_SET_LOAD_STATE;
  payload: { isLoading: boolean };
};

export type PopulateFacilitiesType = {
  type: FacilityStoreActionTypes.POPULATE_FACILITIES;
  payload: { facilities: FacilityInfo[] };
};

export type PopulateFacilityDevicesType = {
  type: FacilityStoreActionTypes.POPULATE_FACILITY_DEVICES;
  payload: { facilityDevices: FacilityDevicesState };
};

export type ClearFacilityDevicesType = {
  type: FacilityStoreActionTypes.CLEAR_FACILITY_DEVICES;
};

export type PopulateFacilityInfo = {
  type: FacilityStoreActionTypes.POPULATE_FACILITY_INFO;
  payload: { facilityInfo: Facility };
};

export type SetErrorAlertType = {
  type: FacilityStoreActionTypes.SET_ERROR_ALERT;
  payload: { showErrorAlert: boolean };
};
export type SetDuplicateAlertType = {
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT;
  payload: { showDuplicateAlert: boolean };
};
export type SetDuplicateAlertTypeFacility = {
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT_FACILITY;
  payload: { showDuplicateAlertFacility: boolean };
};
export type SetDuplicateAlertTypeCareteam = {
  type: FacilityStoreActionTypes.SET_DUPLICATE_ALERT_CARETEAM;
  payload: { showDuplicateAlertCareTeam: boolean };
};

export type SetFacilityAdminsType = {
  type: FacilityStoreActionTypes.SET_FACILITY_ADMINS;
  payload: { facilityId: number; admins: Admin[] };
};

export type SetFacilityInvitesType = {
  type: FacilityStoreActionTypes.SET_FACILITY_INVITES;
  payload: { facilityId: number; invites: FacilityAdminInvite[] };
};

export type SetSuccessAlertType = {
  type: FacilityStoreActionTypes.SET_SUCCESS_ALERT;
  payload: { showSuccessAlert: boolean };
};

export type PopulateCaregiversType = {
  type: FacilityStoreActionTypes.POPULATE_CAREGIVERS;
  payload: { facilityId: number; caregivers: Caregiver[] };
};

export type PopulateCaregiverByIdType = {
  type: FacilityStoreActionTypes.POPULATE_CAREGIVER_BY_ID;
  payload: { caregiver: CaregiverDetails };
};

export type UpdateCaregiverByIdType = {
  type: FacilityStoreActionTypes.UPDATE_CAREGIVER_BY_ID;
  payload: { caregiver: CaregiverDetails };
};

export type DeleteCaregiverByIdType = {
  type: FacilityStoreActionTypes.DELETE_CAREGIVER_BY_ID;
  payload: { customerId: number };
};

export type AddCaregiverType = {
  type: FacilityStoreActionTypes.ADD_CAREGIVER;
  payload: { caregiver: CaregiverDetails };
};

export type SetAddCaregiverSuccessType = {
  type: FacilityStoreActionTypes.SET_ADD_CAREGIVER_SUCCESS;
  payload: { success: boolean };
};

export type SetDeleteCaregiverSuccessType = {
  type: FacilityStoreActionTypes.SET_DELETE_CAREGIVER_SUCCESS;
  payload: { success: boolean };
};

export type ResetAddCaregiverSuccessType = {
  type: FacilityStoreActionTypes.RESET_ADD_CAREGIVER_SUCCESS;
};

export type UpdateFacilityDeviceStatusType = {
  type: FacilityStoreActionTypes.UPDATE_FACILITY_DEVICE_STATUS;
  payload: { serial_number: string; status: string };
};

export type InviteFacilityAdminType = {
  type: FacilityStoreActionTypes.INVITE_FACILITY_ADMIN;
  payload: { email: string; facility_role_id: number };
};

export type SetInviteFacilityAdminSuccessType = {
  type: FacilityStoreActionTypes.SET_INVITE_FACILITY_ADMIN_SUCCESS;
  payload: { success: boolean };
};

export type SetUpdateCaregiverSuccessType = {
  type: FacilityStoreActionTypes.SET_UPDATE_CAREGIVER_SUCCESS;
  payload: { success: boolean };
};

export type ResetSuccessType = {
  type: FacilityStoreActionTypes.RESET_SUCCESS_STATES;
};

export type PopulateParentFacilitiesType = {
  type: FacilityStoreActionTypes.POPULATE_PARENT_FACILITIES;
  payload: { parentFacilities: ParentFacility[] };
};

export type SetAddParentFacilityAdminStatusType = {
  type: FacilityStoreActionTypes.ADD_PARENT_FACILITY_ADMIN_STATUS;
  payload: { success: boolean | null };
};

export type SetFacilitiesBreadcrumbsType = {
  type: FacilityStoreActionTypes.SET_FACILITIES_BREADCRUMBS;
  payload: BreadcrumbItem[];
};

export type PopulateStaffListType = {
  type: FacilityStoreActionTypes.POPULATE_STAFF_LIST;
  payload: { staffList: Staff[] };
};

export type PopulatePatientsListType = {
  type: FacilityStoreActionTypes.POPULATE_PATIENTS_LIST;
  payload: { patientsList: Patient[] };
};

export type PopulateDevicesListType = {
  type: FacilityStoreActionTypes.POPULATE_DEVICES_LIST;
  payload: { devicesList: Device[] };
};

export type PopulateNotesListType = {
  type: FacilityStoreActionTypes.POPULATE_NOTES_LIST;
  payload: { notesList: Note[] };
};

export type AddNoteType = {
  type: FacilityStoreActionTypes.ADD_NOTE;
  payload: { note: Note };
};
export interface SetNoteActionStatus {
  type: FacilityStoreActionTypes.SET_NOTE_ACTION_STATUS;
  payload: {
    success: boolean;
    message: string;
    show: boolean;
  };
}
export interface ClearNoteActionStatus {
  type: FacilityStoreActionTypes.CLEAR_NOTE_ACTION_STATUS;
}

export interface DeactivateFacilityStatusType {
  type: FacilityStoreActionTypes.DEACTIVATE_FACILITY_STATUS;
  payload: { success: boolean };
}
export interface ClearDeactivateFacilityStatusType {
  type: FacilityStoreActionTypes.CLEAR_DEACTIVATE_FACILITY_STATUS;
}

export interface EditStaffStatusType {
  type: FacilityStoreActionTypes.EDIT_STAFF_STATUS;
  payload: { success: boolean };
}

export type FacilityStoreAction =
  | FacilityAddedType
  | ApiErrorType
  | SetLoadStateType
  | PopulateFacilitiesType
  | PopulateFacilityDevicesType
  | ClearFacilityDevicesType
  | PopulateFacilityInfo
  | SetErrorAlertType
  | SetFacilityAdminsType
  | SetFacilityInvitesType
  | SetDuplicateAlertType
  | SetDuplicateAlertTypeFacility
  | SetDuplicateAlertTypeCareteam
  | SetSuccessAlertType
  | PopulateCaregiversType
  | PopulateCaregiverByIdType
  | UpdateCaregiverByIdType
  | DeleteCaregiverByIdType
  | AddCaregiverType
  | SetAddCaregiverSuccessType
  | ResetAddCaregiverSuccessType
  | SetDeleteCaregiverSuccessType
  | UpdateFacilityDeviceStatusType
  | InviteFacilityAdminType
  | SetInviteFacilityAdminSuccessType
  | SetUpdateCaregiverSuccessType
  | ResetSuccessType
  | PopulateParentFacilitiesType
  | SetAddParentFacilityAdminStatusType
  | SetFacilitiesBreadcrumbsType
  | PopulateStaffListType
  | PopulatePatientsListType
  | PopulateDevicesListType
  | PopulateNotesListType
  | AddNoteType
  | SetNoteActionStatus
  | ClearNoteActionStatus
  | DeactivateFacilityStatusType
  | ClearDeactivateFacilityStatusType
  | EditStaffStatusType
  | RequestFacilitySurveyInsightsAction
  | FetchFacilitySurveyInsightsSuccessAction
  | FetchFacilitySurveyInsightsFailureAction;

// Response of GET /facility/:facility_id/survey_insights: the patient
// endpoint's blocks (store/hubOwner/types) plus the location roster size,
// participation per survey type and responders per trend bucket.
export type FacilitySurveyParticipation = {
  // Patients whose assignment to the location overlapped the window: the
  // denominator (not `patient_count`, which is the current roster).
  patients_eligible: number;
  patients_responded: number;
  patients_not_responded: number;
  // patients_responded as a percentage of patients_eligible, one decimal.
  participation_rate: number;
};

export type FacilitySurveyInsightsTrendBucket = SurveyInsightsTrendBucket & {
  // Distinct patients with a counted answer in the bucket.
  patients_responded: number;
};

// One entry of `lowest_scores[]`: a patient of the location who answered the
// survey type in the window, with their lowest counted score and the most
// recent local day they gave it. The API lists every responder, worst first;
// the Insights tab keeps the attention range the Overview dashboard flags.
export type FacilitySurveyLowestScore = {
  customer_id: number;
  first_name: string | null;
  last_name: string | null;
  score: number;
  label: string | null;
  day: string | null;
};

export type FacilitySurveyInsightsBlock = Omit<SurveyInsightsBlock, 'trend'> & {
  participation: FacilitySurveyParticipation;
  trend: FacilitySurveyInsightsTrendBucket[];
  lowest_scores: FacilitySurveyLowestScore[];
};

export type FacilitySurveyInsights = {
  facility_id: number;
  period: SurveyInsightsPeriod;
  // Patients currently assigned to the location (the current roster). The
  // participation denominator is each block's participation.patients_eligible.
  patient_count: number;
  surveys: FacilitySurveyInsightsBlock[];
};

// The location and window the insights were fetched for; the tab compares
// them with its selection to decide whether a refetch is needed.
export type FacilitySurveyInsightsState = FacilitySurveyInsights & {
  facilityId: number;
  requestedDays: number;
};

export type RequestFacilitySurveyInsightsAction = {
  type: FacilityStoreActionTypes.REQUEST_FACILITY_SURVEY_INSIGHTS;
  payload: {
    facilityId: number;
    requestedDays: number;
    granularity: SurveyInsightsGranularity;
    requestId: number;
  };
};

export type FetchFacilitySurveyInsightsSuccessAction = {
  type: FacilityStoreActionTypes.FETCH_FACILITY_SURVEY_INSIGHTS_SUCCESS;
  payload: FacilitySurveyInsightsState & { requestId: number };
};

export type FetchFacilitySurveyInsightsFailureAction = {
  type: FacilityStoreActionTypes.FETCH_FACILITY_SURVEY_INSIGHTS_FAILURE;
  payload: { requestId: number; error: string };
};
